import React, { useState, useRef, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

type FeedbackType = 'bug' | 'feature' | 'vraag' | 'anders';

interface FeedbackWidgetProps {
  userEmail?: string;
  userName?: string;
}

const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({ userEmail, userName }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // === FADE LOGIC: 0% transparant → 70% transparant na 90s → 0% bij hover → 70% na 90s ===
  const [buttonOpacity, setButtonOpacity] = useState(1); // Start volledig zichtbaar
  const [isBtnHovered, setIsBtnHovered] = useState(false);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFadeTimer = () => {
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
  };

  const scheduleFade = () => {
    clearFadeTimer();
    fadeTimerRef.current = setTimeout(() => {
      setButtonOpacity(0.3); // 70% transparant
    }, 90000); // 90 seconden
  };

  // Start initiële fade timer
  useEffect(() => {
    scheduleFade();
    return () => clearFadeTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Block body scroll when modal is open (mobile only, but good practice for all)
  useEffect(() => {
    if (isOpen) {
      // Save current overflow style
      const originalOverflow = document.body.style.overflow;
      // Block scroll
      document.body.style.overflow = 'hidden';
      
      // Restore on cleanup
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const handleFeedbackMouseEnter = () => {
    setIsBtnHovered(true);
    clearFadeTimer();
    setButtonOpacity(1); // Direct volledig zichtbaar
  };

  const handleFeedbackMouseLeave = () => {
    setIsBtnHovered(false);
    scheduleFade(); // Na 90 seconden weer dimmen
  };
  // === END FADE LOGIC ===

  // Debug: Log widget render
  React.useEffect(() => {
    console.log('💬 FeedbackWidget rendered', { userEmail, userName });
  }, [userEmail, userName]);
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('bug');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState(userEmail || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Update email when user props change
  useEffect(() => {
    if (userEmail) setEmail(userEmail);
  }, [userEmail]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const feedbackTypes: { value: FeedbackType; label: string; emoji: string }[] = [
    { value: 'bug', label: 'Bug melden', emoji: '🐛' },
    { value: 'feature', label: 'Idee/Suggestie', emoji: '💡' },
    { value: 'vraag', label: 'Vraag', emoji: '❓' },
    { value: 'anders', label: 'Anders', emoji: '💬' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim()) {
      setError('Vul een bericht in');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Save to Firebase
      const feedbackRef = collection(db, 'feedback');
      await addDoc(feedbackRef, {
        type: feedbackType,
        message: message.trim(),
        email: email.trim() || null,
        userName: userName || null,
        userId: auth.currentUser?.uid || null,
        page: window.location.href,
        userAgent: navigator.userAgent,
        createdAt: serverTimestamp(),
        status: 'nieuw',
      });

      // Create mailto link as backup/notification
      const subject = encodeURIComponent(`[${feedbackType.toUpperCase()}] Community App Feedback`);
      const body = encodeURIComponent(
        `Type: ${feedbackType}\n` +
        `Van: ${userName || 'Anoniem'} (${email || 'geen e-mail'})\n` +
        `Pagina: ${window.location.href}\n\n` +
        `Bericht:\n${message}`
      );
      
      // Open mailto in background (won't navigate away)
      const mailtoLink = `mailto:info@superkonnected.nl?subject=${subject}&body=${body}`;
      
      // Create a hidden iframe to trigger mailto without navigating
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = mailtoLink;
      document.body.appendChild(iframe);
      setTimeout(() => document.body.removeChild(iframe), 1000);

      setSubmitted(true);
      setMessage('');
      setFeedbackType('bug');
      
      // Reset after 3 seconds
      setTimeout(() => {
        setSubmitted(false);
        setIsOpen(false);
      }, 3000);

    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Er ging iets mis. Probeer het opnieuw of mail direct naar info@superkonnected.nl');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Feedback Tab Button - Fixed position right side, below tour button */}
      <div 
        className="feedback-widget-wrapper"
        style={{
          position: 'fixed',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          marginTop: '120px', // Position below tour button
          zIndex: 10011,
          isolation: 'isolate', // Create new stacking context
          willChange: 'transform', // Optimize for performance
        }}
      >
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`
            relative
            flex items-center gap-1.5
            bg-gradient-to-r from-[#0A2342] to-[#1a3a5c]
            hover:from-[#0d2d52] hover:to-[#234a6e]
            text-white text-sm font-medium
            px-2 py-3 rounded-l-lg
            shadow-lg hover:shadow-xl
            ${isOpen ? 'translate-x-full' : 'translate-x-0'}
            writing-mode-vertical
          `}
          style={{ 
            writingMode: 'vertical-rl', 
            textOrientation: 'mixed',
            filter: 'none !important',
            WebkitFilter: 'none !important',
            opacity: isOpen ? 0 : buttonOpacity,
            transition: `opacity ${isBtnHovered ? '0.3s' : '2s'} ease, transform 0.3s ease, box-shadow 0.3s ease`,
          }}
          onMouseEnter={handleFeedbackMouseEnter}
          onMouseLeave={handleFeedbackMouseLeave}
          aria-label="Feedback geven"
          title="Feedback geven"
        >
          <span className="rotate-180">💬</span>
          <span className="rotate-180 tracking-wider">Feedback</span>
        </button>
      </div>

      {/* Feedback Panel */}
      <div
        ref={panelRef}
        className={`
          fixed right-0 top-1/2 -translate-y-1/2 z-[9999]
          w-80 max-w-[calc(100vw-2rem)]
          bg-white rounded-l-2xl shadow-2xl
          border-l border-t border-b border-gray-200
          transform transition-all duration-300 ease-out
          ${isOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white rounded-tl-2xl">
          <div className="flex items-center gap-2">
            <span className="text-xl">💬</span>
            <h3 className="font-semibold text-gray-800">Feedback</h3>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Sluiten"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {submitted ? (
            <div className="text-center py-8">
              <div className="text-5xl mb-3">🎉</div>
              <h4 className="text-lg font-semibold text-gray-800 mb-1">Bedankt!</h4>
              <p className="text-sm text-gray-600">
                Je feedback is ontvangen. We nemen contact op indien nodig.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Feedback Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Soort feedback
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {feedbackTypes.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFeedbackType(type.value)}
                      className={`
                        flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm
                        border transition-all duration-200
                        ${feedbackType === type.value
                          ? 'border-[#0A2342] bg-blue-50 text-[#0A2342] shadow-sm'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                        }
                      `}
                    >
                      <span>{type.emoji}</span>
                      <span>{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="feedback-message" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Jouw bericht
                </label>
                <textarea
                  id="feedback-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={
                    feedbackType === 'bug' 
                      ? 'Beschrijf wat er mis ging...'
                      : feedbackType === 'feature'
                      ? 'Wat zou je graag willen zien?'
                      : feedbackType === 'vraag'
                      ? 'Wat is je vraag?'
                      : 'Vertel ons meer...'
                  }
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm
                    focus:ring-2 focus:ring-[#0A2342] focus:border-[#0A2342]
                    resize-none transition-all"
                  required
                />
              </div>

              {/* Email (optional for non-logged in users) */}
              {!userEmail && (
                <div>
                  <label htmlFor="feedback-email" className="block text-sm font-medium text-gray-700 mb-1.5">
                    E-mail <span className="text-gray-400 font-normal">(optioneel)</span>
                  </label>
                  <input
                    id="feedback-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="jouw@email.nl"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm
                      focus:ring-2 focus:ring-[#0A2342] focus:border-[#0A2342]
                      transition-all"
                  />
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || !message.trim()}
                className={`
                  w-full py-2.5 px-4 rounded-lg font-medium text-sm
                  flex items-center justify-center gap-2
                  transition-all duration-200
                  ${isSubmitting || !message.trim()
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#0A2342] to-[#1a3a5c] hover:from-[#0d2d52] hover:to-[#234a6e] text-white shadow-md hover:shadow-lg'
                  }
                `}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Verzenden...</span>
                  </>
                ) : (
                  <>
                    <span>Versturen</span>
                    <span>→</span>
                  </>
                )}
              </button>

              {/* Privacy note */}
              <p className="text-xs text-gray-400 text-center">
                Je feedback helpt ons de app te verbeteren
              </p>
            </form>
          )}
        </div>
      </div>

      {/* Backdrop for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-[9997] md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default FeedbackWidget;
